import { useState, useEffect } from 'react';
import { X, Users, Crown, Trash2, AlertTriangle } from 'lucide-react';
import { Faction, VictoryType, PlayerRole, ROLE_NAMES, ROLE_FACTION, PenaltyType, PENALTY_NAMES, PENALTY_DESCRIPTIONS } from '../types/stats';
import { DojosRow, SeasonsRow } from '../types/database';
import { PlayerInfo, ScoreCalculation } from '../modules/stats/types';
import { getGoodCount, getEvilCount } from '../modules/stats/calculator';
import { statsDataService } from '../modules/stats/dataService';
import CustomSelect from './CustomSelect';

interface GameRecordModalProps {
  isOpen: boolean;
  onClose: () => void;
  editingGameId: number | null;
  playerCount: number;
  setPlayerCount: (count: number) => void;
  winningFaction: Faction;
  setWinningFaction: (faction: Faction) => void;
  victoryType: VictoryType;
  setVictoryType: (type: VictoryType) => void;
  mvpPlayer: number | '';
  setMvpPlayer: (player: number | '') => void;
  taskResults: (boolean | null)[];
  setTaskResults: (results: (boolean | null)[]) => void;
  playerInfos: PlayerInfo[];
  gameDate: string;
  setGameDate: (date: string) => void;
  gameNumber: number;
  setGameNumber: (num: number) => void;
  gameDojoId: string;
  setGameDojoId: (id: string) => void;
  gameSeasonId: string;
  setGameSeasonId: (id: string) => void;
  calculatedScores: ScoreCalculation[] | null;
  dojos: DojosRow[];
  seasons: SeasonsRow[];
  onSubmit: () => void;
  onDelete: () => void;
  onSaveDraft?: () => void;
  handlePlayerInfoChange: (playerNumber: number, field: keyof PlayerInfo, value: string) => void;
  isSubmitting?: boolean;
  isDeleting?: boolean;
}

const ROLE_OPTIONS: { value: PlayerRole; label: string; faction: Faction }[] = [
  { value: 'merlin', label: '梅林', faction: 'good' },
  { value: 'percival', label: '派西维尔', faction: 'good' },
  { value: 'lancelot_good', label: '好兰斯洛特', faction: 'good' },
  { value: 'loyal', label: '忠臣', faction: 'good' },
  { value: 'mordred', label: '莫德雷德', faction: 'evil' },
  { value: 'morgana', label: '莫甘娜', faction: 'evil' },
  { value: 'assassin', label: '刺客', faction: 'evil' },
  { value: 'minion', label: '爪牙', faction: 'evil' },
  { value: 'oberon', label: '奥伯伦', faction: 'evil' },
];

export default function GameRecordModal({
  isOpen,
  onClose,
  editingGameId,
  playerCount,
  setPlayerCount,
  winningFaction,
  setWinningFaction,
  victoryType,
  setVictoryType,
  mvpPlayer,
  setMvpPlayer,
  taskResults,
  setTaskResults,
  playerInfos,
  gameDate,
  setGameDate,
  gameNumber,
  setGameNumber,
  gameDojoId,
  setGameDojoId,
  gameSeasonId,
  setGameSeasonId,
  calculatedScores,
  dojos,
  seasons,
  onSubmit,
  onDelete,
  onSaveDraft,
  handlePlayerInfoChange,
  isSubmitting = false,
  isDeleting = false,
}: GameRecordModalProps) {
  const [showRoleModal, setShowRoleModal] = useState<number | null>(null);
  const [showMvpModal, setShowMvpModal] = useState(false);
  const [localMvpPlayer, setLocalMvpPlayer] = useState<number | ''>('');
  
  // V251: 特殊扣分规则状态
  const [isPenaltyMode, setIsPenaltyMode] = useState(false);
  const [penaltyNickname, setPenaltyNickname] = useState('');
  const [selectedPenaltyType, setSelectedPenaltyType] = useState<PenaltyType | null>(null);
  const [penaltyReason, setPenaltyReason] = useState('');
  const [penaltyDate, setPenaltyDate] = useState(new Date().toISOString().split('T')[0]);
  const [penaltyPlayerCount, setPenaltyPlayerCount] = useState(5);
  const [penaltyDojoId, setPenaltyDojoId] = useState('');
  const [penaltySeasonId, setPenaltySeasonId] = useState('');
  const [isSavingPenalty, setIsSavingPenalty] = useState(false);

  // V194 FIX: 编辑时带入原记录的 MVP 信息
  useEffect(() => {
    if (isOpen && editingGameId && mvpPlayer) {
      setLocalMvpPlayer(mvpPlayer);
    } else if (!isOpen) {
      setLocalMvpPlayer('');
    }
  }, [isOpen, editingGameId, mvpPlayer]);

  const displayMvpPlayer = editingGameId ? localMvpPlayer : mvpPlayer;

  const handleDisplayMvpChange = (value: number | '') => {
    console.log('[V283] 切换 MVP:', { value, editingGameId, currentValue: displayMvpPlayer });
    if (editingGameId) {
      setLocalMvpPlayer(value);
    }
    setMvpPlayer(value);
  };

  // V251: 切换扣分模式
  const handleTogglePenaltyMode = () => {
    setIsPenaltyMode(!isPenaltyMode);
    // 清空扣分相关状态
    if (!isPenaltyMode) {
      setPenaltyNickname('');
      setSelectedPenaltyType(null);
      setPenaltyReason('');
    }
  };

  // V251: 保存独立扣分记录
  const handleSavePenalty = async () => {
    if (!penaltyNickname.trim()) {
      alert('请输入玩家昵称');
      return;
    }
    if (!selectedPenaltyType) {
      alert('请选择扣分规则');
      return;
    }

    setIsSavingPenalty(true);
    try {
      // 计算扣分分数
      let penaltyScore = 0;
      if (selectedPenaltyType === 'self_destruct') {
        penaltyScore = -(penaltyPlayerCount * 2);
      } else if (selectedPenaltyType === 'wrong_kill') {
        penaltyScore = -penaltyPlayerCount;
      } else if (selectedPenaltyType === 'bug') {
        penaltyScore = -(penaltyPlayerCount * 2);
      }
      
      // 获取当前用户信息（钉钉平台自动设置，支持多种 key 格式）
      const empId = localStorage.getItem('dingtalk_emp_id') || localStorage.getItem('emplId') || '';
      const corpId = localStorage.getItem('dingtalk_corp_id') || localStorage.getItem('corpId') || '';
      
      // V667: 如果没有 emp_id，使用默认值而不是阻止保存
      // 这样可以确保在 localStorage 未设置时仍能正常保存数据
      const finalEmpId = empId || 'system';
      const finalCorpId = corpId || undefined;
      
      console.log('[V251] 保存扣分记录，用户信息:', { 
        empId: finalEmpId, 
        corpId: finalCorpId,
        hasLocalStorage: !!empId 
      });
      
      const result = await statsDataService.savePenaltyRecord({
        player_nickname: penaltyNickname.trim(),
        penalty_type: selectedPenaltyType,
        penalty_score: penaltyScore,
        reason: penaltyReason.trim() || undefined,
        penalty_date: penaltyDate,
        dojo_id: penaltyDojoId ? parseInt(penaltyDojoId) : null,
        season_id: penaltySeasonId ? parseInt(penaltySeasonId) : null,
        player_count: penaltyPlayerCount,
        emp_id: finalEmpId,
        corp_id: finalCorpId,
      });

      if (result.success) {
        // 先清空表单并关闭弹窗
        setPenaltyNickname('');
        setSelectedPenaltyType(null);
        setPenaltyReason('');
        setPenaltyDate(new Date().toISOString().split('T')[0]);
        setPenaltyPlayerCount(5);
        setPenaltyDojoId('');
        setPenaltySeasonId('');
        setIsPenaltyMode(false);
        onClose();
        alert('扣分记录保存成功！触发器将自动更新统计数据');
      } else {
        alert(`保存失败：${result.error}`);
      }
    } catch (err) {
      console.error('[V251] 保存扣分记录异常:', err);
      alert('保存失败，请重试');
    } finally {
      setIsSavingPenalty(false);
    }
  };

  if (!isOpen) return null;

  const goodCount = getGoodCount(playerCount);
  const evilCount = getEvilCount(playerCount);

  // V109: 智能填充忠臣逻辑
  // 当邪恶阵营已满且梅林、派西维尔已分配时，自动填充剩余玩家为忠臣
  useEffect(() => {
    if (!playerInfos.length) return;

    // 统计已分配的角色
    const assignedRoles = playerInfos
      .filter(p => p.player_role)
      .map(p => p.player_role as PlayerRole);

    // 统计邪恶角色数量
    const evilRoles = assignedRoles.filter(role => ROLE_FACTION[role] === 'evil');
    const evilRolesCount = evilRoles.length;

    // 检查是否已分配梅林和派西维尔
    const hasMerlin = assignedRoles.includes('merlin');
    const hasPercival = assignedRoles.includes('percival');

    // 判断条件：邪恶阵营已满 + 梅林和派西维尔已分配
    if (evilRolesCount >= evilCount && hasMerlin && hasPercival) {
      // 找出未分配角色的玩家
      const unassignedPlayers = playerInfos.filter(p => !p.player_role);

      if (unassignedPlayers.length > 0) {
        // 自动填充为忠臣
        unassignedPlayers.forEach(player => {
          handlePlayerInfoChange(player.player_number, 'player_role', 'loyal');
        });
        console.log('[V109] 智能填充：已将', unassignedPlayers.length, '位玩家自动设为忠臣');
      }
    }
  }, [playerInfos, playerCount, evilCount, handlePlayerInfoChange]);

  const filteredSeasons = seasons.filter(s => !gameDojoId || s.dojo_id === Number(gameDojoId));

  const handleRoleSelect = (playerNumber: number, role: PlayerRole) => {
    handlePlayerInfoChange(playerNumber, 'player_role', role);
    setShowRoleModal(null);
  };

  const handleMvpSelect = (playerNum: number | '') => {
    console.log('[V283] handleMvpSelect:', { playerNum, editingGameId });
    if (editingGameId) {
      setLocalMvpPlayer(playerNum);
    }
    setMvpPlayer(playerNum);
    setShowMvpModal(false);
  };

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4 pb-16">
      <div className="bg-avalon-bg-dark rounded-xl max-w-lg w-full max-h-[85vh] overflow-y-auto">
        <div className="p-4 border-b border-avalon-card flex items-center justify-between">
          <h2 className="text-lg font-bold text-white">
            {editingGameId ? '编辑游戏记录' : '录入本局结果'}
          </h2>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-avalon-card text-avalon-text">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-3 space-y-3 pb-4">
          {/* V251: 特殊扣分规则切换开关 */}
          <div className="border border-avalon-purple/30 rounded-lg p-3 bg-gradient-to-r from-red-500/10 to-orange-500/10">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-red-400" />
                <span className="text-white font-bold text-sm">特殊扣分记录</span>
              </div>
              <button
                onClick={handleTogglePenaltyMode}
                className={`relative w-12 h-6 rounded-full transition-colors ${
                  isPenaltyMode ? 'bg-red-500' : 'bg-avalon-card'
                }`}
              >
                <div className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-transform ${
                  isPenaltyMode ? 'left-7' : 'left-1'
                }`} />
              </button>
            </div>
            <p className="text-[10px] text-avalon-text/70">
              {isPenaltyMode ? '当前为独立扣分模式：仅记录扣分项，不保存游戏记录' : '开启后可单独记录玩家扣分项'}
            </p>
          </div>

          {isPenaltyMode ? (
            // V251: 独立扣分记录表单
            <div className="space-y-3">
              <div>
                <label className="block text-avalon-text text-xs mb-1.5">玩家昵称</label>
                <input
                  type="text"
                  value={penaltyNickname}
                  onChange={(e) => setPenaltyNickname(e.target.value)}
                  placeholder="请输入玩家昵称"
                  className="w-full py-1.5 px-2 rounded-lg bg-avalon-card text-white border border-avalon-purple/30 focus:border-avalon-purple outline-none"
                />
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-avalon-text text-xs mb-1.5">日期</label>
                  <input
                    type="date"
                    value={penaltyDate}
                    onChange={(e) => setPenaltyDate(e.target.value)}
                    className="w-full p-2.5 rounded-lg bg-avalon-card text-white border border-avalon-purple/30 focus:border-avalon-purple outline-none"
                  />
                </div>
                <div>
                  <label className="block text-avalon-text text-xs mb-1.5">游戏人数</label>
                  <select
                    value={penaltyPlayerCount}
                    onChange={(e) => setPenaltyPlayerCount(Number(e.target.value))}
                    className="w-full p-2.5 rounded-lg bg-avalon-card text-white border border-avalon-purple/30 focus:border-avalon-purple outline-none"
                  >
                    {[5, 6, 7, 8, 9, 10, 11, 12, 13].map(n => (
                      <option key={n} value={n}>{n}人</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div>
                  <CustomSelect
                    label="道场"
                    value={penaltyDojoId}
                    onChange={(value) => {
                      setPenaltyDojoId(value);
                      if (value && penaltySeasonId) {
                        const currentSeason = seasons.find(s => s.id === Number(penaltySeasonId));
                        if (currentSeason && currentSeason.dojo_id !== Number(value)) {
                          setPenaltySeasonId('');
                        }
                      }
                    }}
                    options={[{ value: '', label: '请选择道场' }, ...dojos.map(d => ({ value: String(d.id), label: d.name || '' }))]}
                  />
                </div>
                <div>
                  <CustomSelect
                    label="赛季"
                    value={penaltySeasonId}
                    onChange={setPenaltySeasonId}
                    options={[
                      { value: '', label: penaltyDojoId ? '请选择赛季' : '请先选择道场' },
                      ...filteredSeasons.map(s => {
                        const dojo = dojos.find(d => d.id === s.dojo_id);
                        return { value: String(s.id), label: dojo ? `${dojo.name} - ${s.name}` : s.name || '' };
                      })
                    ]}
                  />
                </div>
              </div>

              <div>
                <label className="block text-avalon-text text-xs mb-1.5">扣分规则</label>
                <div className="space-y-2">
                  {(['self_destruct', 'wrong_kill', 'bug'] as PenaltyType[]).map((type) => (
                    <button
                      key={type}
                      onClick={() => setSelectedPenaltyType(type)}
                      className={`w-full p-3 rounded-lg border-2 text-left transition-colors ${
                        selectedPenaltyType === type
                          ? 'border-red-500 bg-red-500/20'
                          : 'border-avalon-card bg-avalon-bg-dark/50 hover:border-avalon-purple'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="text-white font-medium text-sm">{PENALTY_NAMES[type]}</div>
                          <div className="text-[10px] text-avalon-text/70 mt-0.5">
                            {PENALTY_DESCRIPTIONS[type]}
                          </div>
                        </div>
                        <div className="text-red-400 font-bold text-sm">
                          -{type === 'wrong_kill' ? penaltyPlayerCount : 2 * penaltyPlayerCount}分
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-avalon-text text-xs mb-1.5">备注说明（可选）</label>
                <textarea
                  value={penaltyReason}
                  onChange={(e) => setPenaltyReason(e.target.value)}
                  placeholder="可填写具体原因"
                  rows={2}
                  className="w-full p-2.5 rounded-lg bg-avalon-card text-white border border-avalon-purple/30 focus:border-avalon-purple outline-none resize-none"
                />
              </div>

              <div className="border border-avalon-purple/30 rounded-lg p-3 bg-avalon-purple/10">
                <div className="text-[10px] text-avalon-text/70 mb-1">扣分预览</div>
                <div className="flex items-center justify-between">
                  <span className="text-white text-sm">
                    {penaltyNickname || '玩家昵称'} 
                    {selectedPenaltyType && ` · ${PENALTY_NAMES[selectedPenaltyType]}`}
                  </span>
                  <span className="text-red-400 font-bold">
                    {selectedPenaltyType ? `-${selectedPenaltyType === 'wrong_kill' ? playerCount : 2 * playerCount}分` : '-?分'}
                  </span>
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-avalon-text text-xs mb-1.5">日期</label>
              <input
                type="date"
                value={gameDate}
                onChange={(e) => setGameDate(e.target.value)}
                className="w-full p-2.5 rounded-lg bg-avalon-card text-white border border-avalon-purple/30 focus:border-avalon-purple outline-none"
              />
            </div>
            <div>
              <label className="block text-avalon-text text-xs mb-1.5">局数</label>
              <input
                type="number"
                min={1}
                value={gameNumber}
                onChange={(e) => setGameNumber(Number(e.target.value))}
                className="w-full p-2.5 rounded-lg bg-avalon-card text-white border border-avalon-purple/30 focus:border-avalon-purple outline-none"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <CustomSelect
              label="道场"
              value={gameDojoId}
              onChange={(value) => {
                setGameDojoId(value);
                if (value && gameSeasonId) {
                  const currentSeason = seasons.find(s => s.id === Number(gameSeasonId));
                  if (currentSeason && currentSeason.dojo_id !== Number(value)) {
                    setGameSeasonId('');
                  }
                }
              }}
              options={[{ value: '', label: '请选择道场' }, ...dojos.map(d => ({ value: String(d.id), label: d.name || '' }))]}
            />
            <CustomSelect
              label="赛季"
              value={gameSeasonId}
              onChange={setGameSeasonId}
              options={[
                { value: '', label: gameDojoId ? '请选择赛季' : '请先选择道场' },
                ...filteredSeasons.map(s => {
                  const dojo = dojos.find(d => d.id === s.dojo_id);
                  return { value: String(s.id), label: dojo ? `${dojo.name} - ${s.name}` : s.name || '' };
                })
              ]}
            />
          </div>

          <div>
            <label className="block text-avalon-text text-xs mb-1">游戏人数</label>
            <div className="grid grid-cols-5 gap-1">
              {[5, 6, 7, 8, 9, 10, 11, 12, 13].map(n => (
                <button
                  key={n}
                  onClick={() => {
                    setPlayerCount(n);
                    handleDisplayMvpChange('');
                  }}
                  className={`py-1 rounded-lg border-2 transition-all ${
                    playerCount === n
                      ? 'border-avalon-gold bg-avalon-gold/20 text-avalon-gold'
                      : 'border-avalon-card bg-avalon-bg-dark/50 text-avalon-text hover:border-avalon-purple'
                  }`}
                >
                  <div className="font-bold text-xs">{n}</div>
                  <div className="text-[9px] opacity-70">{getGoodCount(n)}v{n - getGoodCount(n)}</div>
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-avalon-text text-xs mb-1 flex items-center gap-1">
              <Users className="w-3.5 h-3.5" />
              玩家信息
            </label>
            <div className="text-[10px] text-avalon-text/70 mb-1 bg-avalon-purple/10 p-1 rounded">
              💡 提示：玩家编号可为空或随意填写，系统将自动根据玩家昵称进行统计
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-1 p-1 bg-avalon-bg-dark/50 rounded-lg">
              {playerInfos.map((info) => (
                <div
                  key={info.player_number}
                  className={`p-1.5 rounded-lg border transition-all ${
                    info.player_role
                      ? ROLE_FACTION[info.player_role as PlayerRole] === 'good'
                        ? 'border-blue-500/50 bg-blue-500/10'
                        : 'border-red-500/50 bg-red-500/10'
                      : 'border-avalon-card bg-avalon-bg-dark/50'
                  }`}
                >
                  <div className="flex items-center gap-1.5 mb-1.5">
                    <span className="text-white font-medium text-xs whitespace-nowrap">{info.player_number}号</span>
                    <input
                      type="text"
                      placeholder="昵称"
                      value={info.player_nickname}
                      onChange={(e) => handlePlayerInfoChange(info.player_number, 'player_nickname', e.target.value)}
                      className="flex-1 min-w-0 px-1.5 py-1 rounded bg-avalon-card text-white text-xs border border-avalon-purple/20 focus:border-avalon-purple outline-none truncate"
                    />
                  </div>
                  <button
                    onClick={() => setShowRoleModal(info.player_number)}
                    className={`w-full px-1.5 py-1.5 rounded text-xs border transition-colors ${
                      info.player_role
                        ? ROLE_FACTION[info.player_role as PlayerRole] === 'good'
                          ? 'border-blue-500/50 bg-blue-500/20 text-blue-300'
                          : 'border-red-500/50 bg-red-500/20 text-red-300'
                        : 'border-avalon-card bg-avalon-card text-avalon-text hover:border-avalon-purple'
                    }`}
                  >
                    {info.player_role ? ROLE_NAMES[info.player_role as PlayerRole] : '选角色'}
                  </button>
                </div>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-avalon-text text-xs mb-1">任务进程</label>
            <div className="flex gap-0.5">
              {taskResults.map((result, idx) => (
                <button
                  key={idx}
                  onClick={() => {
                    const newResults = [...taskResults];
                    newResults[idx] = result === null ? true : result === true ? false : null;
                    setTaskResults(newResults);
                  }}
                  className={`flex-1 py-1.5 rounded-lg border-2 transition-colors ${
                    result === true ? 'border-blue-500 bg-blue-500/20 text-blue-400' :
                    result === false ? 'border-red-500 bg-red-500/20 text-red-400' :
                    'border-avalon-card text-avalon-text hover:border-avalon-purple'
                  }`}
                >
                  <div className="text-[10px] opacity-70">第{idx + 1}轮</div>
                  <div className="font-medium text-xs">
                    {result === true ? '成功' : result === false ? '失败' : '未进行'}
                  </div>
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-avalon-text text-xs mb-1">获胜阵营</label>
            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={() => {
                  setWinningFaction('good');
                  handleDisplayMvpChange('');
                }}
                className={`p-2.5 rounded-lg border-2 transition-colors ${
                  winningFaction === 'good'
                    ? 'border-blue-500 bg-blue-500/20 text-blue-400'
                    : 'border-avalon-card text-avalon-text hover:border-avalon-purple'
                }`}
              >
                <div className="font-medium text-sm">正义阵营</div>
                <div className="text-[10px] opacity-70">{goodCount}人</div>
              </button>
              <button
                onClick={() => {
                  setWinningFaction('evil');
                  handleDisplayMvpChange('');
                }}
                className={`p-2.5 rounded-lg border-2 transition-colors ${
                  winningFaction === 'evil'
                    ? 'border-red-500 bg-red-500/20 text-red-400'
                    : 'border-avalon-card text-avalon-text hover:border-avalon-purple'
                }`}
              >
                <div className="font-medium text-sm">邪恶阵营</div>
                <div className="text-[10px] opacity-70">{playerCount - goodCount}人</div>
              </button>
            </div>
          </div>

          <div>
            <label className="block text-avalon-text text-xs mb-1">胜利方式</label>
            {winningFaction === 'good' ? (
              <div className="grid grid-cols-2 gap-2">
                <button
                  onClick={() => setVictoryType('mission')}
                  className={`p-2.5 rounded-lg border-2 transition-colors ${
                    victoryType === 'mission'
                      ? 'border-avalon-gold bg-avalon-gold/20 text-avalon-gold'
                      : 'border-avalon-card text-avalon-text hover:border-avalon-purple'
                  }`}
                >
                  <div className="font-medium text-xs">完成任务梅林隐藏</div>
                </button>
                <button
                  onClick={() => setVictoryType('assassin_fail')}
                  className={`p-2.5 rounded-lg border-2 transition-colors ${
                    victoryType === 'assassin_fail'
                      ? 'border-avalon-gold bg-avalon-gold/20 text-avalon-gold'
                      : 'border-avalon-card text-avalon-text hover:border-avalon-purple'
                  }`}
                >
                  <div className="font-medium text-xs">提前刺杀失败</div>
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-2">
                <button
                  onClick={() => setVictoryType('sabotage')}
                  className={`p-2.5 rounded-lg border-2 transition-colors ${
                    victoryType === 'sabotage'
                      ? 'border-avalon-gold bg-avalon-gold/20 text-avalon-gold'
                      : 'border-avalon-card text-avalon-text hover:border-avalon-purple'
                  }`}
                >
                  <div className="font-medium text-sm">破坏三次任务</div>
                </button>
                <button
                  onClick={() => setVictoryType('assassin_success')}
                  className={`p-2.5 rounded-lg border-2 transition-colors ${
                    victoryType === 'assassin_success'
                      ? 'border-avalon-gold bg-avalon-gold/20 text-avalon-gold'
                      : 'border-avalon-card text-avalon-text hover:border-avalon-purple'
                  }`}
                >
                  <div className="font-medium text-xs">刺杀梅林成功</div>
                </button>
              </div>
            )}
          </div>

          <div>
            <label className="block text-avalon-text text-xs mb-1">
              {winningFaction === 'good' ? '正义阵营 MVP' : '邪恶阵营 MVP'}
            </label>
            <button
              onClick={() => setShowMvpModal(true)}
              className={`w-full p-2 rounded-lg border-2 transition-colors flex items-center justify-between ${
                displayMvpPlayer
                  ? 'border-avalon-gold bg-avalon-gold/20 text-avalon-gold'
                  : 'border-avalon-card bg-avalon-card text-avalon-text hover:border-avalon-purple'
              }`}
            >
              <span>{displayMvpPlayer ? `${displayMvpPlayer}号 MVP` : '无 MVP'}</span>
              <Crown className="w-5 h-5" />
            </button>
          </div>

          {calculatedScores && (
            <div className="border border-avalon-purple/30 rounded-lg p-3">
              <h4 className="text-white font-medium mb-2">积分预览</h4>
              <div className="space-y-1 max-h-40 overflow-y-auto">
                {calculatedScores.map((score) => {
                  const info = playerInfos.find(p => p.player_number === score.player_number);
                  return (
                    <div
                      key={score.player_number}
                      className={`flex items-center justify-between p-2 rounded ${
                        score.faction === 'good' ? 'bg-blue-500/10' : 'bg-red-500/10'
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <span className="text-white text-sm">{score.player_number}号</span>
                        {info?.player_nickname && (
                          <span className="text-xs text-avalon-text">{info.player_nickname}</span>
                        )}
                        <span className={`text-xs px-1.5 py-0.5 rounded ${
                          score.faction === 'good' ? 'bg-blue-500/30 text-blue-300' : 'bg-red-500/30 text-red-300'
                        }`}>
                          {score.faction === 'good' ? '正义' : '邪恶'}
                        </span>
                        {score.is_mvp && (
                          <span className="text-xs bg-avalon-gold/30 text-avalon-gold px-1.5 py-0.5 rounded">MVP</span>
                        )}
                      </div>
                      <span className={`font-bold ${score.total_score >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                        {score.total_score > 0 ? '+' : ''}{score.total_score}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
            </div>
          )}
        </div>
        <div className="p-3 border-t border-avalon-card space-y-2">
          {editingGameId && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onDelete();
              }}
              disabled={isDeleting || isSubmitting}
              className="w-full py-2.5 rounded-lg bg-red-500/20 border border-red-500/50 text-red-400 font-medium hover:bg-red-500/30 transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isDeleting ? (
                <>
                  <div className="w-4 h-4 border-2 border-red-400 border-t-transparent rounded-full animate-spin" />
                  删除中...
                </>
              ) : (
                <>
                  <Trash2 className="w-4 h-4" />
                  删除记录
                </>
              )}
            </button>
          )}
          {!editingGameId && onSaveDraft && !isPenaltyMode && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onSaveDraft();
              }}
              disabled={isSubmitting || isDeleting}
              className="w-full py-2.5 rounded-lg bg-gradient-to-r from-avalon-purple to-purple-600 text-white font-bold shadow-lg hover:from-purple-600 hover:to-avalon-purple transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 transform hover:scale-[1.02]"
            >
              💾 存为草稿
            </button>
          )}
          <div className="flex gap-3">
            <button
              onClick={onClose}
              disabled={isSubmitting || isDeleting || isSavingPenalty}
              className="flex-1 py-2.5 rounded-lg bg-avalon-card text-white font-medium hover:bg-avalon-purple/50 transition-colors disabled:opacity-50"
            >
              取消
            </button>
            {isPenaltyMode ? (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleSavePenalty();
                }}
                disabled={!selectedPenaltyType || !penaltyNickname.trim() || isSavingPenalty}
                className="flex-1 py-2.5 rounded-lg bg-red-500 text-white font-medium hover:bg-red-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isSavingPenalty ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    保存中...
                  </>
                ) : (
                  <>
                    <AlertTriangle className="w-4 h-4" />
                    保存扣分记录
                  </>
                )}
              </button>
            ) : (
              <button
                onClick={onSubmit}
                disabled={!calculatedScores || isSubmitting || isDeleting}
                className="flex-1 py-2.5 rounded-lg bg-avalon-gold text-avalon-bg-dark font-medium hover:bg-yellow-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isSubmitting ? (
                  <>
                    <div className="w-4 h-4 border-2 border-avalon-bg-dark border-t-transparent rounded-full animate-spin" />
                    保存中...
                  </>
                ) : (
                  editingGameId ? '保存修改' : '确认保存'
                )}
              </button>
            )}
          </div>
        </div>
      </div>

      {showRoleModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[60] p-4">
          <div className="bg-[#1E1A30] rounded-xl max-w-sm w-full p-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-white font-bold">选择角色</h3>
              <button onClick={() => setShowRoleModal(null)} className="p-2 rounded-lg hover:bg-avalon-card text-avalon-text">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="space-y-3">
              <div className="text-blue-400 text-sm font-medium">正义阵营</div>
              <div className="grid grid-cols-2 gap-2">
                {ROLE_OPTIONS.filter(r => r.faction === 'good').map(role => (
                  <button
                    key={role.value}
                    onClick={() => handleRoleSelect(showRoleModal, role.value)}
                    className="py-2 px-3 rounded-lg border border-blue-500/30 bg-blue-500/10 text-blue-300 hover:bg-blue-500/20 transition-colors text-sm"
                  >
                    {role.label}
                  </button>
                ))}
              </div>
              <div className="text-red-400 text-sm font-medium mt-4">邪恶阵营</div>
              <div className="grid grid-cols-2 gap-2">
                {ROLE_OPTIONS.filter(r => r.faction === 'evil').map(role => (
                  <button
                    key={role.value}
                    onClick={() => handleRoleSelect(showRoleModal, role.value)}
                    className="py-2 px-3 rounded-lg border border-red-500/30 bg-red-500/10 text-red-300 hover:bg-red-500/20 transition-colors text-sm"
                  >
                    {role.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {showMvpModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[60] p-4">
          <div className="bg-[#1E1A30] rounded-xl max-w-sm w-full p-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-white font-bold flex items-center gap-2">
                <Crown className="w-5 h-5 text-avalon-gold" />
                选择MVP
              </h3>
              <button onClick={() => setShowMvpModal(false)} className="p-2 rounded-lg hover:bg-avalon-card text-avalon-text">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <button
                onClick={() => handleMvpSelect('')}
                className="py-3 px-2 rounded-lg border-2 border-avalon-card bg-avalon-bg-dark/50 text-avalon-text hover:border-avalon-purple transition-colors"
              >
                <div className="text-xs opacity-70">无MVP</div>
              </button>
              {playerInfos
                .filter(p => p.player_role && ROLE_FACTION[p.player_role as PlayerRole] === winningFaction)
                .map(info => (
                  <button
                    key={info.player_number}
                    onClick={() => handleMvpSelect(info.player_number)}
                    className={`py-2 px-2 rounded-lg border-2 transition-colors ${
                      displayMvpPlayer === info.player_number
                        ? 'border-avalon-gold bg-avalon-gold/20 text-avalon-gold'
                        : 'border-avalon-card bg-avalon-bg-dark/50 text-white hover:border-avalon-purple'
                    }`}
                  >
                    <div className="font-bold">{info.player_number}号</div>
                    {info.player_nickname && <div className="text-xs opacity-70 truncate">{info.player_nickname}</div>}
                  </button>
                ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
